import React, { useState, useEffect, useRef } from 'react';
import TextInput from './TextInput';
import { validatePhoneNumber } from '../../utils/phoneUtils';

const PhoneInput = ({ 
  placeholder = "Enter phone number", 
  inputClassName = "", 
  onChange, 
  defaultValue = "", 
  disabled = false,
  required = false,
  label = "Phone Number",
  onVerify,
  showVerifyButton = false,
  isVerifying = false
}) => {
  const [phoneNumber, setPhoneNumber] = useState(defaultValue || "");
  const [isValid, setIsValid] = useState(true);
  const [lastInputLength, setLastInputLength] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null && defaultValue !== "") {
      console.log('PhoneInput - Setting defaultValue:', defaultValue); // Debug log
      console.log('PhoneInput - defaultValue type:', typeof defaultValue); // Debug log
      console.log('PhoneInput - defaultValue length:', defaultValue?.toString().length); // Debug log
      
      // Check if defaultValue is a valid string (not [object Object])
      const stringValue = String(defaultValue);
      if (stringValue === '[object Object]') {
        console.log('PhoneInput - Invalid defaultValue detected, ignoring');
        return;
      }
      
      // Always update when defaultValue changes (for initial load)
      setPhoneNumber(stringValue);
      setLastInputLength(stringValue.length);
    }
  }, [defaultValue]); // Remove phoneNumber from dependencies to prevent infinite loop

  // Removed cursor position management to prevent focus issues

  const formatPhoneNumber = (value) => {
    // Handle null, undefined, or empty values
    if (!value || value === null || value === undefined) {
      return '';
    }
    
    // Convert to string if it's not already
    const stringValue = String(value);
    
    // Remove all non-digit characters except +
    let cleaned = stringValue.replace(/[^\d+]/g, '');
    
    // Handle international format starting with +
    if (cleaned.startsWith('+')) {
      // For international numbers, allow up to 15 digits after +
      const digitsAfterPlus = cleaned.substring(1).replace(/\D/g, '');
      if (digitsAfterPlus.length <= 15) {
        return cleaned;
      }
      return cleaned.substring(0, 16); // +15 digits max
    }
    
    // Handle US/local format (9-11 digits)
    const digits = cleaned.replace(/\D/g, '');
    if (digits.length <= 11) {
      // Always format when we have enough digits, regardless of current formatting
      if (digits.length >= 9) {
        // For 9-digit numbers, format as XXX-XXX-XXX
        if (digits.length === 9) {
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}`;
        } else if (digits.length === 10) {
          // For 10-digit numbers, format as XXX-XXX-XXXX
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
        } else if (digits.length === 11) {
          // For 11-digit numbers, format as XXX-XXX-XXXX-X
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}-${digits.slice(10, 11)}`;
        }
      } else if (digits.length >= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length >= 3) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        // For very short inputs, just return the digits as-is
        return digits;
      }
    }
    
    // If it's a US number and has more than 11 digits, return raw digits
    if (digits.length > 11) {
      return digits; 
    }
    
    return digits.slice(0, 11);
  };

  // Smart formatting that preserves user intent
  const smartFormatPhoneNumber = (value, oldValue) => {
    const cleaned = value.replace(/\D/g, '');
    
    // If user is adding digits to a fully formatted number, be smart about it
    if (oldValue && oldValue.match(/^\d{3}-\d{3}-\d{4}$/) && value.length > oldValue.length) {
      // User is adding digits to a complete US format
      if (cleaned.length > 10) {
        // Return raw digits as user requested
        return cleaned;
      }
    }
    
    // Check if we're transitioning from formatted to raw input
    if (cleaned.length > 10 && !value.startsWith('+')) {
      // For US numbers exceeding 10 digits, show raw digits
      return cleaned;
    }
    
    // Removed maintainFormatting logic
    
    // Always try to format when we have 9-11 digits (US format)
    if (cleaned.length === 9 && !value.startsWith('+')) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 9)}`;
    }
    
    if (cleaned.length === 10 && !value.startsWith('+')) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
    
    if (cleaned.length === 11 && !value.startsWith('+')) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}-${cleaned.slice(10, 11)}`;
    }
    
    // For partial US numbers (less than 9 digits), apply partial formatting
    if (cleaned.length < 9 && !value.startsWith('+')) {
      if (cleaned.length >= 6) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      } else if (cleaned.length >= 3) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
      }
    }
    
    // Otherwise, use normal formatting
    return formatPhoneNumber(value);
  };

  const validatePhoneNumber = (value) => {
    // Handle null, undefined, or empty values
    if (!value || value === null || value === undefined) {
      return false;
    }
    
    // Convert to string if it's not already
    const stringValue = String(value);
    
    // Remove formatting for validation
    const cleaned = stringValue.replace(/[^\d+]/g, '');
    
    // International format (starts with +)
    if (cleaned.startsWith('+')) {
      const digitsAfterPlus = cleaned.substring(1);
      return digitsAfterPlus.length >= 10 && digitsAfterPlus.length <= 15;
    }
    
    // US/local format (accept 9-11 digits for flexibility)
    return cleaned.length >= 9 && cleaned.length <= 11;
  };

  // Removed cursor position tracking to prevent focus issues

  const handleChange = (value) => {
    console.log('PhoneInput handleChange - value:', value, 'type:', typeof value);
    
    // Handle null, undefined, or empty values
    if (value === null || value === undefined) {
      return;
    }
    
    // Handle [object Object] case
    const checkValue = String(value);
    if (checkValue === '[object Object]') {
      console.log('PhoneInput handleChange - Invalid value detected, ignoring');
      return;
    }
    
    if (value === '') {
      setPhoneNumber('');
      setIsValid(false);
      setLastInputLength(0);
      if (onChange) {
        onChange('');
      }
      return;
    }
    
    // Convert to string and trim whitespace
    const stringValue = String(value).trim();
    
    // For partial input (less than 9 digits), just show what user typed
    const cleaned = stringValue.replace(/\D/g, '');
    
    if (cleaned.length < 9) {
      setPhoneNumber(stringValue);
      setIsValid(false);
      if (onChange) {
        onChange(stringValue);
      }
      setLastInputLength(stringValue.length);
      return;
    }
    
    // Only format complete numbers (9+ digits)
    let displayValue = stringValue;
    let isValidPhone = false;
    
    if (cleaned.length === 10) {
      displayValue = `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      isValidPhone = true;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const withoutOne = cleaned.slice(1);
      displayValue = `${withoutOne.slice(0, 3)}-${withoutOne.slice(3, 6)}-${withoutOne.slice(6, 10)}`;
      isValidPhone = true;
    } else if (cleaned.length === 9) {
      displayValue = `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 9)}`;
      isValidPhone = true;
    } else {
      // For other cases, just show the raw input
      displayValue = stringValue;
      isValidPhone = /^\d{3}-\d{3}-\d{4}$/.test(displayValue) || 
                     /^\d{3}-\d{3}-\d{3}$/.test(displayValue) ||
                     /^\d{3}-\d{3}-\d{4}-\d$/.test(displayValue);
    }
    
    setPhoneNumber(displayValue);
    setIsValid(isValidPhone);
    
    if (onChange) {
      onChange(displayValue);
    }
    
    setLastInputLength(stringValue.length);
  };

  const getInputClassName = () => {
    let className = inputClassName;
    
    if (!isValid && phoneNumber && phoneNumber.length > 0) {
      className += " border-red-500 focus:border-red-500";
    } else if (isValid && phoneNumber && phoneNumber.length > 0) {
      className += " border-green-500 focus:border-green-500";
    }
    
    return className;
  };

  return (
    <div className="w-full">
      <span className="text-sm mb-3 block text-white">
        {typeof label === 'string' ? label.replace(/\*$/, '') : label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>
      
      <div className="relative">
                       <TextInput
                 placeholder={placeholder}
                 inputClassName={getInputClassName()}
                 onChange={handleChange}
                 value={phoneNumber}
                 disabled={disabled}
               />
        {phoneNumber && phoneNumber.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setPhoneNumber('');
              setIsValid(false);
              setLastInputLength(0);
              if (onChange) {
                onChange('');
              }
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white transition-colors"
            title="Clear phone number"
          >
            ✕
          </button>
        )}
      </div>
      
      {/* Help text and verification button */}
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-white/60">
          {phoneNumber && phoneNumber.length > 0 && phoneNumber.startsWith('+') 
            ? "International format: +12345678901234"
            : "US format: 123-456-7890"
          }
        </span>
        
        {/* Verification button */}
        {showVerifyButton && phoneNumber && phoneNumber.length > 0 && isValid && (
          <button
            type="button"
            onClick={() => onVerify && onVerify(phoneNumber)}
            disabled={isVerifying || disabled}
            className="text-xs bg-primary text-white px-3 py-1 rounded hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isVerifying ? "Verifying..." : "Verify"}
          </button>
        )}
      </div>
      
      {/* Simple validation message */}
      {phoneNumber && phoneNumber.length > 0 && (
        <span className={`text-xs mt-1 block ${isValid ? 'text-green-400' : 'text-red-400'}`}>
          {isValid ? '✓ Valid phone number' : 'Please enter a valid phone number'}
        </span>
      )}
    </div>
  );
};

export default PhoneInput; 